/**
 * Catch-all redirect for a module's own <Routes>: `<Route path="*" element={<SplatRedirect />} />`.
 *
 * React Router 7 resolves a relative link inside a splat route from the FULL address (React Router 6
 * used only the part before the splat; this is the v7_relativeSplatPath change in the v6 -> v7
 * upgrade guide). Under v7, `<Navigate to="." />` in a `*` route points at the unknown address itself,
 * and `<Navigate to="pending" />` nests under it. This component works out the module root (the
 * address with the splat part removed) and redirects there, or to `to` under it, which is exactly
 * what those catch-all routes did under React Router 6. Module mount points stay where App.tsx puts them.
 */

import { Navigate, useLocation, useParams } from 'react-router';

/** The address with the trailing splat segments removed, e.g. ('/rae/a/b', 'a/b') -> '/rae'. */
export function splatBase(pathname: string, splat: string): string {
  const drop = splat.split('/').filter(Boolean).length;
  const segs = pathname.split('/').filter(Boolean);
  return `/${segs.slice(0, Math.max(0, segs.length - drop)).join('/')}`;
}

export function SplatRedirect({ to = '' }: { to?: string }): JSX.Element {
  const { pathname } = useLocation();
  const splat = useParams()['*'] ?? '';
  const base = splatBase(pathname, splat);
  const target = to ? `${base === '/' ? '' : base}/${to}` : base;
  return <Navigate to={target} replace />;
}
